/**
 * Utility page-title-notifier
 * @description global utility for notifying a user on another tab that this tab has updates
 * @copyright 2019-3-20
 * @file
 */

/**
 * The Page Title Notifier utility accepts a configuration object used for changing the browser's
 * page title, defaulting to a loop between new and old message. Primary use is for notifying a user
 * of an event that has occured that requires their attention.
 */

const DEFAULTS = {
  interval: 1500,
  loop: true
};
let oldTitle = document.title;
let intervalID;
let notifierOptions = {
  ...DEFAULTS
};
let timeoutID;
let isDismissed = false;

// Set the name of the hidden property and the change event for visibility, dependent on browser
let hidden = '';
let visibilityChange = '';
if (typeof document.hidden !== 'undefined') {
  // Opera 12.10 and Firefox 18 and later support
  hidden = 'hidden';
  visibilityChange = 'visibilitychange';
} else if (typeof document.msHidden !== 'undefined') {
  hidden = 'msHidden';
  visibilityChange = 'msvisibilitychange';
} else if (typeof document.webkitHidden !== 'undefined') {
  hidden = 'webkitHidden';
  visibilityChange = 'webkitvisibilitychange';
}

/**
 * Handler for the tab being hidden
 * @returns {void}
 */
function handleVisibilityChange() {
  if (isDismissed) return;
  if (document[hidden] && notifierOptions.loop && notifierOptions.message) {
    // Start interval to display message
    intervalID = setInterval(() => {
      document.title = document.title === oldTitle ? notifierOptions.message : oldTitle;
    }, notifierOptions.interval);
  } else if (document[hidden] && !notifierOptions.loop && notifierOptions.message) {
    // Change title and keep it changed
    timeoutID = setTimeout(() => {
      document.title = notifierOptions.message;
    }, notifierOptions.interval);
  } else {
    // Return to original title and clear interval/timeout
    clearInterval(intervalID);
    clearTimeout(timeoutID);
    document.title = oldTitle;
    isDismissed = true;
  }
}

/**
 * Binds a MutationObserver to watch for page title updates to preserve new title after
 * notifier intervals.
 *
 * @returns {void}
 */
function bindTitleObserver() {
  const pageTitle = document.querySelector('title');
  if (!pageTitle) return;
  const observerConfig = {
    childList: true,
    subtree: true,
    characterData: true
  };
  const titleObserver = new MutationObserver(mutationList => {
    const newTitle = mutationList[0].target.textContent;
    if (oldTitle === newTitle) return;
    if (notifierOptions.message === newTitle) return;
    oldTitle = newTitle;
  });
  titleObserver.observe(pageTitle, observerConfig);
}

/**
 * Method to manually trigger the visibilityChange event programmatically. Useful for triggering
 * the event in a timeout while the page is out of focus.
 *
 * @returns {void}
 */
function triggerVisiblityEvent() {
  let event;
  if (navigator.userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > -1) {
    // User is using Internet Explorer, dispatch event the old fashioned way
    event = document.createEvent('Event');
    event.initEvent(visibilityChange, true, true);
  } else {
    event = new Event(visibilityChange);
  }
  document.dispatchEvent(event);
}

/**
 * Initializes the page title notifier utility, accepting a config object. Depending on the
 * config's loop property, the utility either returns an interval or a timeout back to the caller
 * for later manipulation if desired.
 *
 * @param {Object} config config object passed for creation
 * @returns {void}
 */
function init(config) {
  notifierOptions = {
    ...DEFAULTS,
    ...config
  };
  document.addEventListener(visibilityChange, handleVisibilityChange, false);
  bindTitleObserver();
}

/**
 * Method for allowing updates to the interval configuration on demand.
 * Removed visibility listener, then reactivated it with new config.
 *
 * @param {Object} updateOpts object containing updated parameters for the notifier
 * @returns {void}
 */
function updateIntervalOptions(updateOpts) {
  isDismissed = false;
  clearInterval(intervalID);
  document.removeEventListener(visibilityChange, handleVisibilityChange, false);
  Object.assign(notifierOptions, updateOpts);
  document.addEventListener(visibilityChange, handleVisibilityChange, false);
  if (document[hidden]) {
    // Manually trigger visibilty event to get updated message onto the screen
    triggerVisiblityEvent();
  }
}

/**
 * Sets page title back to original and clears intervals.
 *
 * @returns {void}
 */
function resetTitle() {
  clearInterval(intervalID);
  document.removeEventListener(visibilityChange, handleVisibilityChange, false);
  if (document.title !== oldTitle) {
    document.title = oldTitle;
  }
}

export { init, resetTitle, updateIntervalOptions };
